import { useEffect, useRef, useState, type ReactElement } from 'react';
import { toast } from '../../../../design-system/components';
import { trackFailed } from '../../../../../../shared-web/track';
import { apiErrorCode, apiFailureReason, emberApi, isAuthRequired, type GroupEmoji, type GroupPage as GroupData, type GroupPost, type GroupReaction } from '../../api';
import { BottomSheet } from '../../components/BottomSheet';
import { Link } from '../../components/Link';
import { PageFailure } from '../../components/PageFailure';
import { PageLoading } from '../../components/PageLoading';
import { plural } from '../../format';
import { navigate } from '../../router';
import { BandArt, FaceStack, GroupBar, GroupFaceView, GroupReactions, MORE, ago, useGroupsLive } from './group-parts';
import { withMine } from './group-reactions';

const WEEK_MS = 7 * 86400e3;

/**
 * A group — `/groups/:groupId` (2026-11-03 founder; mock Group / NewPost). Its name, a few of the people in it, how many
 * there are and what is new this week. Not joined yet: what it is about and Join. Joined: every post ever made, newest
 * first, each inviting a read — who and when, the question, the start of the background, the first answer quoted and the
 * faces of who answered, or the wait for a first answer with a way to give it. "Ask or share" puts a post up; the menu
 * leaves the group.
 */
export function GroupPage({ groupId }: { groupId: string }): ReactElement {
  const path = `/groups/${encodeURIComponent(groupId)}`;
  const [page, setPage] = useState<GroupData | null>(null);
  const [failed, setFailed] = useState<'' | 'not_open' | 'load_failed'>('');
  const [sheet, setSheet] = useState<'' | 'new' | 'leave'>('');
  const [busy, setBusy] = useState(false);

  const reads = useRef(0);
  const load = (): void => {
    const seq = ++reads.current;
    emberApi.group(groupId)
      .then((r) => { if (seq === reads.current) { setPage(r); setFailed(''); } })
      .catch((err: unknown) => {
        if (seq !== reads.current) return;
        if (isAuthRequired(err)) { navigate(`/auth?returnTo=${encodeURIComponent(path)}`, { replace: true }); return; }
        if (apiErrorCode(err) === 'not_open') { setFailed('not_open'); return; }
        trackFailed('group_page', apiFailureReason(err), { match_id: groupId });
        setFailed('load_failed');
      });
  };
  useEffect(load, [groupId]);
  useGroupsLive(load);

  const join = async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    try {
      await emberApi.joinGroup(groupId);
      load();
    } catch (err) {
      if (isAuthRequired(err)) { navigate(`/auth?returnTo=${encodeURIComponent(path)}`, { replace: true }); return; }
      trackFailed('group_join', apiFailureReason(err), { match_id: groupId });
      if (apiErrorCode(err) === 'not_open') setFailed('not_open');
      else toast.error('Couldn’t join just now. Try again?');
    } finally {
      setBusy(false);
    }
  };

  // An emoji on a post, from its card: drawn at once, the server's answer for the post then stands.
  const setPostReactions = (postId: string, list: GroupReaction[]): void => {
    setPage((cur) => (cur ? { ...cur, posts: cur.posts.map((p) => (p.id === postId ? { ...p, reactions: list } : p)) } : cur));
  };
  const react = async (post: GroupPost, emoji: string | null): Promise<void> => {
    setPostReactions(post.id, withMine(post.reactions, emoji));
    try {
      const out = await emberApi.reactInGroup(post.id, emoji);
      setPostReactions(post.id, out.reactions);
    } catch (err) {
      trackFailed('group_react', apiFailureReason(err), { match_id: groupId, state: 'feed' });
      setPostReactions(post.id, post.reactions);
      toast.error('Couldn’t add that. Try again?');
    }
  };

  const leave = async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    try {
      await emberApi.leaveGroup(groupId);
      setSheet('');
      navigate('/groups', { replace: true });
    } catch (err) {
      trackFailed('group_leave', apiFailureReason(err), { match_id: groupId });
      toast.error('Couldn’t leave just now. Try again?');
    } finally {
      setBusy(false);
    }
  };

  if (failed === 'not_open') return <PageFailure title="This group isn’t open to you" detail="Groups open up based on what you’ve talked about with Ember." logState="not_open" />;
  if (!page && failed) return <PageFailure title="Couldn’t open this group" logState="load_failed" retry />;
  if (!page) return <PageLoading />;

  const { group, posts } = page;
  const since = Date.now() - WEEK_MS;
  const week = posts.filter((p) => p.at && new Date(p.at).getTime() > since).length;
  const people = group.joined
    ? (group.people > 1 ? `${group.people} of you here` : 'Just you so far')
    : (group.people > 0 ? `${plural(group.people, 'person', 'people')} here` : 'Be the first one here');
  const here = [people, week > 0 ? `${week} new this week` : ''].filter(Boolean).join(' · ');

  return (
    <main className="nf-page gr-page" data-log-seen="group_page" data-log-state={group.joined ? 'joined' : 'not_joined'} data-log-match-id={groupId}>
      <GroupBar to="/groups" right={group.joined ? (
        <button type="button" className="nf-iconbtn" aria-label="Leave group" data-log="group_leave" data-log-state="ask" data-log-match-id={groupId} onClick={() => setSheet('leave')}>
          {MORE}
        </button>
      ) : null} />
      <div className="nf-head gr-group-head">
        <h1 className="nf-title is-step" dir="auto">{group.name}</h1>
        <div className="gr-here">
          {group.joined && <FaceStack faces={group.faces ?? []} size={28} you />}
          <span>{here}</span>
        </div>
      </div>

      {!group.joined ? (
        <div className="nf-main gr-join-main">
          {group.about && <p className="gr-about" dir="auto">{group.about}</p>}
          <p className="gr-as">You’ll show up as <strong dir="auto">{group.me.name}</strong>, never your real name.</p>
          <button type="button" className="nf-cta is-block" disabled={busy} data-log="group_join" data-log-state="page" data-log-match-id={groupId} onClick={() => { void join(); }}>
            Join
          </button>
        </div>
      ) : (
        <div className="nf-main gr-feed">
          {posts.length === 0
            ? <p className="gr-empty">Nothing here yet. Be the first to ask or share.</p>
            : posts.map((p) => <PostCard key={p.id} post={p} emoji={page.emoji} onReact={(e) => { void react(p, e); }} />)}
        </div>
      )}

      {group.joined && (
        <button type="button" className="gr-fab" data-log="group_ask_or_share" data-log-match-id={groupId} onClick={() => setSheet('new')}>
          <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden><path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" /></svg>
          <span>Ask or share</span>
        </button>
      )}

      {sheet === 'new' && (
        <BottomSheet title="What would you like to post?" className="gr-sheet" logSeen="group_chooser" logSeenAttrs={{ 'data-log-match-id': groupId }} onClose={() => setSheet('')}>
          <Link to={`${path}/new?kind=question`} className="gr-choice is-peach" data-log="group_choose" data-log-state="question" data-log-match-id={groupId}>
            <span className="gr-choice-art"><BandArt tone="peach" mark="question" /></span>
            <span className="gr-choice-text">
              <span className="gr-choice-title">Ask a question</span>
              <span className="gr-choice-sub">You’d like answers. Say your question, then a little background.</span>
            </span>
          </Link>
          <Link to={`${path}/new?kind=share`} className="gr-choice is-sage" data-log="group_choose" data-log-state="share" data-log-match-id={groupId}>
            <span className="gr-choice-art"><BandArt tone="sage" mark="heart" /></span>
            <span className="gr-choice-text">
              <span className="gr-choice-title">Share what’s going on</span>
              <span className="gr-choice-sub">No answer needed. Just get it off your chest. People can still react or reply.</span>
            </span>
          </Link>
          <p className="gr-choice-as">
            <GroupFaceView face={group.me} size={26} />
            <span>You’ll show up as <strong dir="auto">{group.me.name}</strong>. Only the group sees it.</span>
          </p>
        </BottomSheet>
      )}

      {sheet === 'leave' && (
        <BottomSheet
          title="Leave this group?"
          description="You won’t see it anymore, and you can’t rejoin on your own. What you posted stays."
          logSeen="group_leave_sheet"
          logSeenAttrs={{ 'data-log-match-id': groupId }}
          onClose={() => setSheet('')}
        >
          <button type="button" className="ember-btn nf-btn-dark" disabled={busy} data-log="group_leave" data-log-state="confirm" data-log-match-id={groupId} onClick={() => { void leave(); }}>
            Leave group
          </button>
          <button type="button" className="ember-btn ember-btn-ghost" data-log="group_leave" data-log-state="keep" onClick={() => setSheet('')}>
            Stay
          </button>
        </BottomSheet>
      )}
    </main>
  );
}

/**
 * One post in the feed. A question: who asks and when, the question, the start of the background, then the first answer
 * quoted with the faces of who answered — or, with none yet, the wait and a way to answer. A share: who shares, what they
 * wrote, its replies. Under each, its emoji (a tap there reacts too).
 */
function PostCard({ post, emoji, onReact }: { post: GroupPost; emoji: GroupEmoji | undefined; onReact: (emoji: string | null) => void }): ReactElement {
  const to = `/groups/p/${encodeURIComponent(post.id)}`;
  const mine = post.author.me;
  const verb = post.kind === 'question' ? (mine ? 'You asked' : 'asks') : (mine ? 'You shared' : 'shares');
  const when = ago(post.at);
  const first = post.first_answer;
  const waiting = post.kind === 'question' && !first;
  return (
    <article className="gr-post">
      <Link to={to} className="gr-post-open" data-log="group_post_open" data-log-state={post.kind === 'share' ? 'share' : first ? 'answered' : 'waiting'} data-log-match-id={post.group_id}>
        <span className="gr-post-who">
          <GroupFaceView face={post.author} size={26} />
          <span className="gr-post-by">
            {mine ? verb : <><strong dir="auto">{post.author.name}</strong> {verb}</>}{when ? ` · ${when}` : ''}
          </span>
        </span>
        {post.kind === 'share'
          ? <span className="gr-post-share" dir="auto">{post.body}</span>
          : (
            <>
              <span className="gr-post-question" dir="auto">{post.question}</span>
              {post.body && <span className="gr-post-body" dir="auto">{post.body}</span>}
            </>
          )}
        {first && (
          <span className="gr-quote">
            <GroupFaceView face={first.author} size={24} />
            <span className="gr-quote-text" dir="auto">“{first.text}”</span>
          </span>
        )}
        {post.answer_count > 0 && (
          <span className="gr-post-meta">
            <FaceStack faces={post.answered_by.slice(0, 3)} size={22} />
            <span className="gr-post-count">{post.kind === 'share' ? plural(post.answer_count, 'reply', 'replies') : plural(post.answer_count, 'answer')}</span>
            <span className="gr-post-read">Read all</span>
          </span>
        )}
        {waiting && <span className="gr-post-waiting">Waiting for its first answer</span>}
      </Link>
      <div className="gr-post-foot">
        <GroupReactions reactions={post.reactions} emoji={emoji} onReact={onReact} groupId={post.group_id} what="post" />
        {waiting && !mine && (
          <Link to={`${to}?answer=1`} className="gr-outline" data-log="group_answer_open" data-log-state="feed" data-log-match-id={post.group_id}>Answer</Link>
        )}
      </div>
    </article>
  );
}
